import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import { SiteShell } from "@/components/site-shell";
import { useAuth } from "@/hooks/use-auth";
import { resolveNextPath } from "@/lib/auth-redirect";
import { GoogleAuthButton } from "@/components/google-auth-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [{ title: "Log in — DealDraft" }],
    links: [{ rel: "canonical", href: "https://dealdraft.pro/login" }],
  }),
  validateSearch: z.object({ next: z.string().optional() }).catch({}),
  component: LoginPage,
});

function LoginPage() {
  const { supabase } = useAuth();
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Welcome back");
    const { to, search } = resolveNextPath(next);
    navigate({ to: to as "/" | "/generate" | "/dashboard", search: search as never });
  }

  async function onForgot() {
    if (!supabase || !email) {
      toast.error("Enter your email above first.");
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) toast.error(error.message);
    else toast.success("Check your inbox for the reset link.");
  }

  return (
    <SiteShell>
      <section className="mx-auto flex max-w-md flex-col px-6 py-16">
        <h1 className="text-3xl font-semibold tracking-tight">Welcome back</h1>
        <p className="mt-2 text-sm text-muted-foreground">Log in to access your dashboard.</p>

        <div className="mt-8 space-y-4 rounded-xl border border-border bg-card p-6 shadow-sm">
          <GoogleAuthButton next={next} />
          <div className="relative py-1 text-center text-xs text-muted-foreground">
            <span className="bg-card px-2">or continue with email</span>
            <div className="absolute inset-x-0 top-1/2 -z-10 border-t border-border" />
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                data-testid="login-email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button
                  type="button"
                  onClick={onForgot}
                  className="text-xs text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={submitting || !supabase}
              data-testid="login-submit"
            >
              {submitting ? "Logging in…" : "Log in"}
            </Button>
          </form>
        </div>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Don't have an account?{" "}
          <Link to="/signup" className="font-medium text-primary hover:underline">
            Sign up
          </Link>
        </p>
      </section>
    </SiteShell>
  );
}
